// ===============================================
// 🚀 ADMIN DASHBOARD OPTIMIZADO - AdminDashboard.tsx
// ===============================================
import React, { useState, useEffect, useMemo } from 'react';
import Configuration from './SupportSettings';
import { useI18n } from '../hooks/useI18n';
import toast from 'react-hot-toast';
import SystemHealth from './SystemHealth';
import { 
  Database, Search, BarChart3, RefreshCw, Trash2, Eye, Calendar, Users, Activity, 
  Server, Clock, TrendingUp, Settings, UserPlus, Edit, Shield, CreditCard, 
  FileText, Upload, Download, Plus, X, CheckCircle, XCircle, Crown, DollarSign, 
  HelpCircle, Save, AlertTriangle, Filter, MoreHorizontal, ChevronLeft, 
  ChevronRight, Mail, Phone, Building, Globe, Lock, Key, Copy, ExternalLink,
  FileDown, FileUp, Search as SearchIcon, SortAsc, SortDesc, RotateCcw,
  Archive, Star, Award, Target, Zap, MessageSquare, Headphones, Smartphone
} from 'lucide-react';
import { 
  adminUserService, 
  adminPlanService, 
  adminPaymentService, 
  adminDocumentService, 
  adminFaqService, 
  adminStatsService,
  adminExportService 
} from '../services/adminApiService';
// ===============================================
// 🔧 INTERFACES COMPLETAS Y OPTIMIZADAS
// ===============================================

interface Equipment {
  id: number;
  serial_number: string;
  model: string;
  equipment_type: string;
  status: string;
  last_scraped: string;
  scrape_success: boolean;
  parts_count: number;
  sections_count: number;
}

interface SearchStat {
  date: string;
  total_searches: number;
  cache_hits: number;
  avg_processing_time: number;
  unique_queries: number;
}

interface PopularQuery {
  query_text: string;
  search_count: number;
  avg_results: number;
  last_searched: string;
}

interface SystemStats {
  server: {
    uptime: number;
    memory: any;
    version: string;
  };
  database: {
    status: string;
    stats: {
      total_equipment: number;
      total_parts: number;
      searches_today: number;
    };
  };
  ai: {
    status: string;
  };
}

interface User {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'user' | 'premium' | 'moderator';
  subscription_status: 'active' | 'inactive' | 'expired' | 'trial' | 'suspended' | 'pending';
  subscription_type: string;
  created_at: string;
  last_login?: string;
  queries_used: number;
  queries_limit: number;
  is_premium: boolean;
  phone?: string;
  company?: string;
}

interface Plan {
  id: string;
  name: string;
  description?: string;
  price: number;
  currency: string;
  interval: 'monthly' | 'yearly';
  features: string[];
  queries_limit: number;
  is_active: boolean;
  created_at: string;
  priority_support?: boolean;
  storage_limit?: number;
}

interface Payment {
  id: string;
  user_id: string;
  user_email: string;
  plan_name: string;
  amount: number;
  currency: string;
  status: 'completed' | 'pending' | 'failed' | 'refunded' | 'cancelled';
  payment_method: string;
  created_at: string;
  invoice_url?: string;
  transaction_id?: string;
}

interface Document {
  id: string;
  title: string;
  filename: string;
  file_path: string;
  file_size: number;
  file_type: string;
  category: string;
  uploaded_by: string;
  uploaded_at: string;
  downloads: number;
  is_public: boolean;
  description?: string;
  tags?: string[];
  version?: string;
}

interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: string;
  order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  tags?: string[];
  helpfulness?: number;
}

// ✅ INTERFACES PARA VALIDACIÓN Y FORMULARIOS
interface FormErrors {
  [key: string]: string;
}

interface PaginationState {
  currentPage: number;
  itemsPerPage: number;
  totalItems: number;
  totalPages: number;
}

interface SortState {
  column: string;
  direction: 'asc' | 'desc';
}

interface SearchFilters {
  query: string;
  dateFrom?: string;
  dateTo?: string;
  status?: string;
  category?: string;
}

const AdminDashboard: React.FC = () => {
  // ✅ HOOK DE TRADUCCIÓN
  const { t } = useI18n();
  
  // ===============================================
  // 🎯 ESTADOS PRINCIPALES
  // ===============================================
  
  // Estados originales
  const [activeTab, setActiveTab] = useState<'overview' | 'equipment' | 'searches' | 'users' | 'plans' | 'payments' | 'documents' | 'faq' | 'config'>('overview');
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [searchStats, setSearchStats] = useState<SearchStat[]>([]);
  const [popularQueries, setPopularQueries] = useState<PopularQuery[]>([]);
  const [systemStats, setSystemStats] = useState<SystemStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const [activeSection, setActiveSection] = useState<string>('users'); 
  // Estados para datos
const [plans, setPlans] = useState([]);
const [payments, setPayments] = useState([]);
const [documents, setDocuments] = useState([]);
const [loadingDocs, setLoadingDocs] = useState(false);
const [faqs, setFaqs] = useState([]);
const [users, setUsers] = useState([]);


// Estados de paginación
const [paymentsPagination, setPaymentsPagination] = useState({
  currentPage: 1,
  totalItems: 0,
  totalPages: 0,
  itemsPerPage: 10
});

const [documentsPagination, setDocumentsPagination] = useState({
  currentPage: 1,
  totalItems: 0,
  totalPages: 0,
  itemsPerPage: 12
});


 
  // ✅ ESTADOS DE FORMULARIOS Y MODALES OPTIMIZADOS
  const [showUserModal, setShowUserModal] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showFaqModal, setShowFaqModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [editingFaq, setEditingFaq] = useState<FAQ | null>(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [newDocument, setNewDocument] = useState({
  file: null as File | null,
  title: '',
  description: '',
  category: 'manual' as 'manual' | 'bulletin' | 'specification' | 'parts_list',
  tags: [] as string[]
});

  const handleDocumentDelete = async (docId) => {
    try {
      await adminDocumentService.deleteDocument(docId);
      await loadDocuments(documentsPagination.currentPage, docFilters);
      addNotification('success', 'Documento eliminado exitosamente');
    } catch (error) {
      console.error('Error deleting document:', error);
      addNotification('error', error.message);
    }
  };
  // ✅ ESTADOS DE VALIDACIÓN
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ✅ ESTADOS DE PAGINACIÓN
  const [usersPagination, setUsersPagination] = useState<PaginationState>({
    currentPage: 1, itemsPerPage: 10, totalItems: 0, totalPages: 0
  });
 
  // ✅ ESTADOS DE ORDENAMIENTO
  const [usersSort, setUsersSort] = useState<SortState>({ column: 'created_at', direction: 'desc' });
  const [paymentsSort, setPaymentsSort] = useState<SortState>({ column: 'created_at', direction: 'desc' });

  // ✅ ESTADOS DE BÚSQUEDA Y FILTROS
  const [searchFilters, setSearchFilters] = useState<SearchFilters>({ query: '' });
  const [userFilters, setUserFilters] = useState({ status: 'all', role: 'all', subscription: 'all' });
  const [paymentFilters, setPaymentFilters] = useState({ status: 'all', method: 'all' });
  const [docFilters, setDocFilters] = useState({ category: 'all', visibility: 'all' });

  // ✅ ESTADOS DE EXPORTACIÓN
  const [isExporting, setIsExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState<'csv' | 'excel' | 'pdf'>('csv');

  // ✅ ESTADOS DE SELECCIÓN MÚLTIPLE
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [selectedPayments, setSelectedPayments] = useState<Set<string>>(new Set());
  const [selectedDocuments, setSelectedDocuments] = useState<Set<string>>(new Set());

  // ✅ ESTADOS DE NOTIFICACIONES
  const [notifications, setNotifications] = useState<Array<{
    id: string;
    type: 'success' | 'error' | 'info';
    message: string;
  }>>([]);

  const darkMode = document.documentElement.classList.contains('dark');
  const API_BASE = import.meta.env.VITE_API_URL || '/api';

  // ✅ ESCUCHADOR DE CAMBIOS DE TEMA
  const [, forceUpdate] = useState({});

// Componente Toast
  const ToastNotifications = () => (
  <div className="fixed top-4 right-4 z-50 space-y-2">
    {notifications.map((notification) => (
      <div
        key={notification.id}
        className={`flex items-center space-x-3 px-4 py-3 rounded-lg shadow-lg border max-w-md ${
          notification.type === 'success'
            ? 'bg-green-50 border-green-200 text-green-800'
            : notification.type === 'error'
            ? 'bg-red-50 border-red-200 text-red-800'
            : 'bg-blue-50 border-blue-200 text-blue-800'
        }`}
      >
        {notification.type === 'success' ? (
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
        ) : notification.type === 'error' ? (
          <XCircle className="w-5 h-5 flex-shrink-0" />
        ) : (
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
        )}
        <span className="text-sm font-medium flex-1">{notification.message}</span>
        <button
          onClick={() => setNotifications(prev => prev.filter(n => n.id !== notification.id))}
          className="text-gray-400 hover:text-gray-600 flex-shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    ))}
  </div>
);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      forceUpdate({});
    });
    
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
  // ✅ VERIFICACIÓN INICIAL Y CARGA DE DATOS
    const checkAdminAccess = () => {
    const token = localStorage.getItem('kikai_token') || 
                  localStorage.getItem('token') ||
                  sessionStorage.getItem('token');
    
    if (!token) {
      console.error('❌ [ADMIN-ACCESS] No token found');
      window.location.href = '/login';
      return false;
    }
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const now = Date.now() / 1000;
      
      // ✅ VERIFICAR EXPIRACIÓN CON MARGEN DE 60 SEGUNDOS
      if (payload.exp <= (now + 60)) {
        console.error('❌ [ADMIN-ACCESS] Token expired or expiring soon');
        localStorage.removeItem('token');
        localStorage.removeItem('kikai_token');
        sessionStorage.removeItem('token');
        window.location.href = '/login';
        return false;
      }
      
      // ✅ VERIFICACIÓN DE ROL MEJORADA
      console.log('🔍 [ADMIN-ACCESS] Token payload:', {
        email: payload.email,
        role: payload.role,
        hasRole: payload.role !== undefined,
        isAdmin: payload.role === 'admin'
      });
      
      // ✅ PERMITIR ACCESO SI:
      // 1. Tiene role='admin' explícito
      // 2. No tiene role (para compatibilidad temporal)
      // 3. Es un admin conocido por email
      const isExplicitAdmin = payload.role === 'admin';
      const isKnownAdmin = payload.email?.includes('admin') || payload.email?.includes('tecnico');
      const hasNoRole = !payload.role; // Temporal hasta corregir backend
      
      if (isExplicitAdmin) {
        console.log('✅ [ADMIN-ACCESS] Explicit admin role found');
      } else if (isKnownAdmin && hasNoRole) {
        console.log('✅ [ADMIN-ACCESS] Known admin email without role (dev mode)');
      } else if (hasNoRole) {
        console.log('⚠️ [ADMIN-ACCESS] No role in token - allowing for development');
        console.log('🚨 [ADMIN-ACCESS] SECURITY: This should be fixed in production!');
      } else {
        console.error('❌ [ADMIN-ACCESS] Admin role required');
        console.error('🔍 [ADMIN-ACCESS] Found role:', payload.role);
        addNotification('error', 'Se requieren permisos de administrador');
        return false;
      }
      
      console.log('✅ [ADMIN-ACCESS] Access granted for:', payload.email);
      return true;
      
    } catch (error) {
      console.error('❌ [ADMIN-ACCESS] Invalid token:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('kikai_token');
      sessionStorage.removeItem('token');
      window.location.href = '/login';
      return false;
    }
  };
  
  // ✅ VERIFICAR ACCESO INICIAL
  if (checkAdminAccess()) {
    loadDashboardData();
  }

  // ✅ VERIFICACIÓN PERIÓDICA CADA 30 SEGUNDOS
  const tokenCheckInterval = setInterval(() => {
    if (!checkAdminAccess()) {
      clearInterval(tokenCheckInterval);
    }
  }, 30000); // 30 segundos

  // ✅ LIMPIAR INTERVALO AL DESMONTAR
  return () => {
    clearInterval(tokenCheckInterval);
  };
}, []); // Solo al montar el componente
  // ===============================================
  // 🔄 FUNCIONES DE CARGA DE DATOS OPTIMIZADAS
  // ===============================================

const loadDashboardData = async () => {
  setIsLoading(true);
  try {
    await Promise.all([
      loadSystemStats().catch(error => {
        console.error('Error loading system stats:', error);
      }),
      loadSearchStats().catch(error => {
        console.error('Error loading search stats:', error);
      }),
      loadPopularQueries().catch(error => {
        console.error('Error loading popular queries:', error);
      }),
      loadEquipment().catch(error => {
        console.error('Error loading equipment:', error);
      }),
      // ✅ AGREGAR LAS FUNCIONES QUE FALTAN:
      loadUsers().catch(error => {
        console.error('Error loading users:', error);
      }),
      loadPlans().catch(error => {
        console.error('Error loading plans:', error);
      }),
      loadPayments().catch(error => {
        console.error('Error loading payments:', error);
      }),
      loadDocuments().catch(error => {
        console.error('Error loading documents:', error);
      }),
      loadFaqs().catch(error => {
        console.error('Error loading FAQs:', error);
      })
    ]);
    console.log('✅ [DASHBOARD] Todos los datos cargados');
  } catch (error) {
    console.error('❌ [DASHBOARD] Error general loading data:', error);
    addNotification('error', 'Error cargando datos del dashboard');
  } finally {
    setIsLoading(false);
  }
};

 // Funciones de carga existentes (mantener igual)
const loadSystemStats = async () => {
  try {
    const response = await adminStatsService.getSystemStats();
    setSystemStats(response.stats);
  } catch (error) {
    console.error('Error loading system stats:', error);
    addNotification('error', error.message);
  }
};

// ✅ REEMPLAZAR loadSearchStats()
const loadSearchStats = async () => {
  try {
    const response = await adminStatsService.getSearchStats();
    setSearchStats(response.stats || []);
  } catch (error) {
    console.error('Error loading search stats:', error);
    setSearchStats([]);
  }
};

// ✅ REEMPLAZAR loadPopularQueries()
const loadPopularQueries = async () => {
  try {
    const response = await adminStatsService.getPopularQueries();
    setPopularQueries(response.queries || []);
  } catch (error) {
    console.error('Error loading popular queries:', error);
    setPopularQueries([]);
  }
};

// ✅ REEMPLAZAR loadEquipment()
const loadEquipment = async () => {
  try {
    const response = await adminStatsService.getEquipment();
    setEquipment(response.equipment || []);
  } catch (error) {
    console.error('Error loading equipment:', error);
    setEquipment([]);
  }
};

// ✅ REEMPLAZAR loadUsers()
const loadUsers = async (page = 1, filters = userFilters, sort = usersSort) => {
  try {
    const params = {
      page: page.toString(),
      limit: usersPagination.itemsPerPage.toString(),
      sortBy: sort.column,
      sortOrder: sort.direction,
      ...Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== 'all')
      ),
      search: searchFilters.query
    };

    const response = await adminUserService.getUsers(params);
    
    setUsers(response.users || []);
    setUsersPagination(prev => ({
      ...prev,
      currentPage: page,
      totalItems: response.pagination?.totalItems || 0,
      totalPages: response.pagination?.totalPages || 0
    }));
  } catch (error) {
    console.error('Error loading users:', error);
    setUsers([]);
    addNotification('error', error.message);
  }
};

// ✅ REEMPLAZAR loadPlans()
const loadPlans = async () => {
  try {
    const response = await adminPlanService.getPlans();
    setPlans(response.plans || []);
  } catch (error) {
    console.error('Error loading plans:', error);
    setPlans([]);
    addNotification('error', error.message);
  }
};

// ✅ REEMPLAZAR loadPayments()
const loadPayments = async (page = 1, filters = paymentFilters, sort = paymentsSort) => {
  try {
    const params = {
      page: page.toString(),
      limit: paymentsPagination.itemsPerPage.toString(),
      sortBy: sort.column,
      sortOrder: sort.direction,
      ...Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== 'all')
      ),
      search: searchFilters.query
    };

    const response = await adminPaymentService.getPayments(params);
    
    setPayments(response.payments || []);
    setPaymentsPagination(prev => ({
      ...prev,
      currentPage: page,
      totalItems: response.pagination?.totalItems || 0,
      totalPages: response.pagination?.totalPages || 0
    }));
  } catch (error) {
    console.error('Error loading payments:', error);
    setPayments([]);
    addNotification('error', error.message);
  }
};

// ✅ REEMPLAZAR loadDocuments()
const loadDocuments = async (page = 1, filters = docFilters) => {
  try {
    const params = {
      page: page.toString(),
      limit: documentsPagination.itemsPerPage.toString(),
      ...Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== 'all')
      ),
      search: searchFilters.query
    };

    console.log('📄 [LOAD-DOCS] Cargando documentos con params:', params);
    
    const response = await adminDocumentService.getDocuments(params);
    
    console.log('📦 [LOAD-DOCS] Respuesta del servidor:', response);
    console.log('📋 [LOAD-DOCS] Documentos recibidos:', response.documents);
    
    setDocuments(response.documents || []);
    setDocumentsPagination(prev => ({
      ...prev,
      currentPage: page,
      totalItems: response.pagination?.totalItems || 0,
      totalPages: response.pagination?.totalPages || 0
    }));
  } catch (error) {
    console.error('❌ Error loading documents:', error);
    setDocuments([]);
    addNotification('error', error.message);
  }
};


// ✅ REEMPLAZAR loadFaqs()
const loadFaqs = async () => {
  try {
    const response = await adminFaqService.getFaqs();
    setFaqs(response.faqs || []);
  } catch (error) {
    console.error('Error loading FAQs:', error);
    setFaqs([]);
    addNotification('error', error.message);
  }
};

  // ✅ CARGAR DATOS AL INICIO
  useEffect(() => {
    loadDashboardData();
  }, []);

  // ✅ EFECTOS PARA BÚSQUEDA EN TIEMPO REAL
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (activeTab === 'users') {
        loadUsers(1, userFilters, usersSort);
      } else if (activeTab === 'payments') {
        loadPayments(1, paymentFilters, paymentsSort);
      } else if (activeTab === 'documents') {
        loadDocuments(1, docFilters);
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchFilters.query]);

  // ===============================================
  // 🔧 UTILIDADES DE VALIDACIÓN
  // ===============================================

  const validateUserForm = (data: Partial<User>): FormErrors => {
    const errors: FormErrors = {};
    
    if (!data.name?.trim()) {
      errors.name = t('admin.users.form.name') + ' es requerido';
    }
    
    if (!data.email?.trim()) {
      errors.email = t('admin.users.form.email') + ' es requerido';
    } else if (!/\S+@\S+\.\S+/.test(data.email)) {
      errors.email = 'Email inválido';
    }
    
    if (!data.role) {
      errors.role = t('admin.users.form.role') + ' es requerido';
    }
    
    return errors;
  };

  const validatePlanForm = (data: Partial<Plan>): FormErrors => {
    const errors: FormErrors = {};
    
    if (!data.name?.trim()) {
      errors.name = t('admin.plans.form.name') + ' es requerido';
    }
    
    if (!data.price || data.price <= 0) {
      errors.price = 'Precio debe ser mayor a 0';
    }
    
    if (!data.interval) {
      errors.interval = t('admin.plans.form.interval') + ' es requerido';
    }
    
    return errors;
  };

  const validateFaqForm = (data: Partial<FAQ>): FormErrors => {
    const errors: FormErrors = {};
    
    if (!data.question?.trim()) {
      errors.question = t('admin.faq.form.question') + ' es requerida';
    }
    
    if (!data.answer?.trim()) {
      errors.answer = t('admin.faq.form.answer') + ' es requerida';
    }
    
    if (!data.category?.trim()) {
      errors.category = t('admin.faq.form.category') + ' es requerida';
    }
    
    return errors;
  };

  // ===============================================
  // ⚡ FUNCIONES DE ACCIONES OPTIMIZADAS
  // ===============================================

  const handleUserAction = async (action, userId, data) => {
  if (action !== 'delete' && data) {
    const errors = validateUserForm(data);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
  }

  setIsSubmitting(true);
  try {
    let response;
    
    switch (action) {
      case 'create':
        response = await adminUserService.createUser(data);
        break;
      case 'update':
        response = await adminUserService.updateUser(userId, data);
        break;
      case 'delete':
        response = await adminUserService.deleteUser(userId);
        break;
      default:
        throw new Error(`Unknown action: ${action}`);
    }
    
    await loadUsers(usersPagination.currentPage, userFilters, usersSort);
    setShowUserModal(false);
    setEditingUser(null);
    setFormErrors({});
    addNotification('success', response.message || t('admin.notifications.userCreated'));
    
  } catch (error) {
    console.error(`Error ${action} user:`, error);
    addNotification('error', error.message || t('admin.notifications.error'));
  } finally {
    setIsSubmitting(false);
  }
};

const handlePlanAction = async (action, planId, data = null) => {
  setIsSubmitting(true);
  try {
    let response;
    switch (action) {
      case 'create':
        response = await adminPlanService.createPlan(data);
        break;
      case 'update':
        response = await adminPlanService.updatePlan(planId, data);
        break;
      case 'delete':
        response = await adminPlanService.deletePlan(planId);
        break;
    }
    
    await loadPlans();
    setShowPlanModal(false);
    setEditingPlan(null);
    setFormErrors({});
    
    const message = action === 'delete' ? 'Plan eliminado exitosamente' :
                   action === 'create' ? 'Plan creado exitosamente' :
                   'Plan actualizado exitosamente';
    addNotification('success', message);
    
  } catch (error) {
    console.error(`Error ${action} plan:`, error);
    addNotification('error', error.message || `Error al ${action} el plan`);
  } finally {
    setIsSubmitting(false);
  }
};

const handleFaqAction = async (action, faqId, data = null) => {
  setIsSubmitting(true);
  try {
    let response;
    switch (action) {
      case 'create':
        response = await adminFaqService.createFaq(data);
        break;
      case 'update':
        response = await adminFaqService.updateFaq(faqId, data);
        break;
      case 'delete':
        response = await adminFaqService.deleteFaq(faqId);
        break;
    }
    
    await loadFaqs();
    setShowFaqModal(false);
    setEditingFaq(null);
    setFormErrors({});
    
    const message = action === 'delete' ? 'FAQ eliminada exitosamente' :
                   action === 'create' ? 'FAQ creada exitosamente' :
                   'FAQ actualizada exitosamente';
    addNotification('success', message);
    
  } catch (error) {
    console.error(`Error ${action} FAQ:`, error);
    addNotification('error', error.message || `Error al ${action} la FAQ`);
  } finally {
    setIsSubmitting(false);
  }
};

const handleDocumentUpload = async (file: File, metadata: any) => {
  if (!file) {
    toast.error('Por favor selecciona un archivo');
    return;
  }

  setUploadingDoc(true);
  try {
    const API_BASE = import.meta.env.VITE_API_URL || 'https://app.ingeai7.com/api';
    const token = localStorage.getItem('kikai_token') || localStorage.getItem('token');

    const CHUNK_SIZE = 500 * 1024; // 500KB por chunk
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const uploadId = Date.now() + '-' + Math.random().toString(36).substring(7);

    console.log(`📦 [UPLOAD] Subiendo en ${totalChunks} chunks de 500KB`);

    // Subir cada chunk
    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);

      const formData = new FormData();
      formData.append('chunk', chunk);
      formData.append('uploadId', uploadId);
      formData.append('chunkIndex', i.toString());
      formData.append('totalChunks', totalChunks.toString());
      formData.append('filename', file.name);
      formData.append('mimetype', file.type);

      // Solo en el último chunk, enviar metadata
      if (i === totalChunks - 1) {
        formData.append('metadata', JSON.stringify({
          title: metadata.title || file.name,
          description: metadata.description || '',
          category: metadata.category || 'manual',
          version: metadata.version || '1.0',
          is_public: metadata.is_public ?? true
        }));
      }

      const response = await fetch(`${API_BASE}/admin/documents/upload-chunk`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || `Error en chunk ${i + 1}`);
      }

      console.log(`✅ [UPLOAD] Chunk ${i + 1}/${totalChunks} subido`);
    }

    toast.success('Documento subido exitosamente');
    setShowDocumentModal(false);
    await loadDocuments(1, docFilters);

  } catch (error: any) {
    console.error('❌ Error uploading document:', error);
    toast.error(error.message || 'Error al subir el documento');
  } finally {
    setUploadingDoc(false);
  }
};

// ✅ REEMPLAZAR handleExport()
const handleExport = async (type, format = exportFormat) => {
  setIsExporting(true);
  try {
    const filters = type === 'users' ? userFilters : paymentFilters;
    
    if (type === 'users') {
      await adminExportService.exportUsers(format, filters);
    } else if (type === 'payments') {
      await adminExportService.exportPayments(format, filters);
    }
    
    addNotification('success', t('admin.notifications.dataExported'));
  } catch (error) {
    console.error('Error exporting data:', error);
    addNotification('error', error.message || t('admin.notifications.error'));
  } finally {
    setIsExporting(false);
  }
};

// ✅ REEMPLAZAR handleBulkAction()
const handleBulkAction = async (action, type) => {
  const selected = type === 'users' ? selectedUsers : 
                  type === 'payments' ? selectedPayments : selectedDocuments;
  
  if (selected.size === 0) return;

  const confirmed = window.confirm(
    t('admin.users.confirmations.bulkDelete', { count: selected.size })
  );
  
  if (!confirmed) return;

  try {
    if (type === 'users' && action === 'delete') {
      await adminUserService.bulkDeleteUsers(Array.from(selected));
      await loadUsers(usersPagination.currentPage, userFilters, usersSort);
      setSelectedUsers(new Set());
    }
    
    addNotification('success', t('admin.notifications.saved'));
  } catch (error) {
    console.error(`Error bulk ${action}:`, error);
    addNotification('error', error.message || t('admin.notifications.error'));
  }
};

  // ✅ FUNCIÓN DE NOTIFICACIONES
  const addNotification = (type: 'success' | 'error' | 'info', message: string) => {
    const id = Date.now().toString();
    setNotifications(prev => [...prev, { id, type, message }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  };

const refreshData = async () => {
  // ✅ VERIFICAR TOKEN ANTES DE REFRESH
  const token = localStorage.getItem('kikai_token') || 
                localStorage.getItem('token') ||
                sessionStorage.getItem('token');
                
  if (!token) {
    console.error('❌ [REFRESH] No token found during refresh');
    window.location.href = '/login';
    return;
  }

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const now = Date.now() / 1000;
    
    if (payload.exp <= (now + 30)) {
      console.error('❌ [REFRESH] Token expired during refresh');
      localStorage.removeItem('token');
      localStorage.removeItem('kikai_token');
      sessionStorage.removeItem('token');
      window.location.href = '/login';
      return;
    }
  } catch {
    console.error('❌ [REFRESH] Invalid token during refresh');
    window.location.href = '/login';
    return;
  }

  setRefreshing(true);
  try {
    await loadDashboardData();
    addNotification('success', 'Datos actualizados correctamente');
  } catch (error) {
    console.error('❌ [REFRESH] Error during refresh:', error);
    addNotification('error', 'Error actualizando datos');
  } finally {
    setRefreshing(false);
  }
};

  // ===============================================
  // 🎨 UTILIDADES DE FORMATO Y CLASES
  // ===============================================

  const formatUptime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    return `${minutes}m`;
  };

  const formatMemory = (bytes: number) => {
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatFileSize = (bytes: number) => {
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;
    
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  };

  // Clases dinámicas para tema
  const bgClass = darkMode ? 'bg-gray-900' : 'bg-gray-50';
  const cardClass = darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const textClass = darkMode ? 'text-white' : 'text-gray-900';
  const mutedTextClass = darkMode ? 'text-gray-300' : 'text-gray-600';
  const buttonPrimaryClass = darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-600 hover:bg-blue-700';
  const buttonSecondaryClass = darkMode ? 'bg-gray-600 hover:bg-gray-700' : 'bg-gray-600 hover:bg-gray-700';
  const inputClass = darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900';

  // ===============================================
  // 🖥️ COMPONENTES DE UTILIDAD
  // ===============================================

  // ✅ COMPONENTE DE PAGINACIÓN
  const PaginationComponent: React.FC<{
    pagination: PaginationState;
    onPageChange: (page: number) => void;
    onItemsPerPageChange: (items: number) => void;
  }> = ({ pagination, onPageChange, onItemsPerPageChange }) => (
    <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
      <div className="flex items-center space-x-2">
        <select
          value={pagination.itemsPerPage}
          onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
          className={`px-3 py-1 rounded border ${inputClass} text-sm`}
        >
          <option value={5}>5</option>
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
        </select>
        <span className={`text-sm ${mutedTextClass}`}>
          {t('admin.pagination.rowsPerPage')}
        </span>
      </div>
      
      <div className="flex items-center space-x-2">
        <span className={`text-sm ${mutedTextClass}`}>
          {t('admin.pagination.showing', {
            start: Math.min((pagination.currentPage - 1) * pagination.itemsPerPage + 1, pagination.totalItems),
            end: Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems),
            total: pagination.totalItems
          })}
        </span>
      </div>
      
      <div className="flex items-center space-x-1">
        <button
          onClick={() => onPageChange(pagination.currentPage - 1)}
          disabled={pagination.currentPage <= 1}
          className={`px-3 py-1 rounded border ${
            pagination.currentPage <= 1 
              ? 'opacity-50 cursor-not-allowed' 
              : 'hover:bg-gray-50 dark:hover:bg-gray-700'
          } ${cardClass} ${textClass}`}
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        
        {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
          const page = i + Math.max(1, pagination.currentPage - 2);
          if (page > pagination.totalPages) return null;
          
          return (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              className={`px-3 py-1 rounded border ${
                page === pagination.currentPage 
                  ? 'bg-blue-600 text-white border-blue-600' 
                  : `hover:bg-gray-50 dark:hover:bg-gray-700 ${cardClass} ${textClass}`
              }`}
            >
              {page}
            </button>
          );
        })}
        
        <button
          onClick={() => onPageChange(pagination.currentPage + 1)}
          disabled={pagination.currentPage >= pagination.totalPages}
          className={`px-3 py-1 rounded border ${
            pagination.currentPage >= pagination.totalPages 
              ? 'opacity-50 cursor-not-allowed' 
              : 'hover:bg-gray-50 dark:hover:bg-gray-700'
          } ${cardClass} ${textClass}`}
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  // ✅ COMPONENTE DE BÚSQUEDA
  const SearchComponent: React.FC<{
    placeholder: string;
    onSearch: (query: string) => void;
    filters?: React.ReactNode;
  }> = ({ placeholder, onSearch, filters }) => (
    <div className="flex items-center space-x-4 mb-6">
      <div className="relative flex-1 max-w-md">
        <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder={placeholder}
          value={searchFilters.query}
          onChange={(e) => {
            setSearchFilters(prev => ({ ...prev, query: e.target.value }));
            onSearch(e.target.value);
          }}
          className={`pl-10 pr-4 py-2 w-full rounded-lg border ${inputClass} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
        />
      </div>
      {filters}
    </div>
  );

  // ✅ COMPONENTE DE NOTIFICACIONES
  const NotificationComponent: React.FC = () => (
    <div className="fixed top-4 right-4 space-y-2 z-50">
      {notifications.map((notification) => (
        <div
          key={notification.id}
          className={`px-4 py-3 rounded-lg shadow-lg border flex items-center space-x-3 ${
            notification.type === 'success' 
              ? 'bg-green-50 border-green-200 text-green-800'
              : notification.type === 'error'
              ? 'bg-red-50 border-red-200 text-red-800'
              : 'bg-blue-50 border-blue-200 text-blue-800'
          }`}
        >
          {notification.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : notification.type === 'error' ? (
            <XCircle className="w-5 h-5" />
          ) : (
            <AlertTriangle className="w-5 h-5" />
          )}
          <span className="text-sm font-medium">{notification.message}</span>
          <button
            onClick={() => setNotifications(prev => prev.filter(n => n.id !== notification.id))}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  );

  // ===============================================
  // 🖥️ TABS DE NAVEGACIÓN
  // ===============================================

const tabs = [
  { key: 'overview', label: t('admin.tabs.overview'), icon: BarChart3 },
  { key: 'users', label: t('admin.tabs.users'), icon: Users },
  { key: 'plans', label: t('admin.tabs.plans'), icon: Crown },
  { key: 'payments', label: t('admin.tabs.payments'), icon: CreditCard },
  { key: 'documents', label: t('admin.tabs.documents'), icon: FileText },
  { key: 'faq', label: t('admin.tabs.faq'), icon: HelpCircle },
  { key: 'equipment', label: 'Equipamiento', icon: Database },
  { key: 'searches', label: 'Búsquedas', icon: Search },
  { key: 'system', label: 'Sistema', icon: Server },
  { key: 'config', label: 'Configuración', icon: Settings }
];

  const renderConfigTab = () => {
    if (activeTab !== 'config') return null;
    
    return (
      <div className="w-full">
        <Configuration darkMode={darkMode} />
      </div>
    );
  };

  // ===============================================
  // 📊 RENDERIZADO PRINCIPAL
  // ===============================================

  return (
    <div className={`min-h-screen p-6 ${bgClass}`}>
     {/* ✅ AGREGAR NOTIFICACIONES TOAST */}
    <ToastNotifications />
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className={`text-3xl font-bold ${textClass}`}>
              {t('admin.title')}
            </h1>
            <p className={`${mutedTextClass} mt-1`}>
              {t('admin.subtitle')}
            </p>
          </div>
          
          <div className="flex gap-3">
            {/* ✅ BOTONES DE EXPORTACIÓN */}
            {(activeTab === 'users' || activeTab === 'payments') && (
              <div className="flex items-center space-x-2">
                <select
                  value={exportFormat}
                  onChange={(e) => setExportFormat(e.target.value as any)}
                  className={`px-3 py-2 rounded-lg border ${inputClass} text-sm`}
                >
                  <option value="csv">CSV</option>
                  <option value="excel">Excel</option>
                  <option value="pdf">PDF</option>
                </select>
                <button
                  onClick={() => handleExport(activeTab as 'users' | 'payments')}
                  disabled={isExporting}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg ${buttonSecondaryClass} text-white text-sm`}
                >
                  <FileDown className="w-4 h-4" />
                  {isExporting ? t('admin.export.generating') : t('admin.actions.export')}
                </button>
              </div>
            )}

            {/* ✅ ACCIONES MÚLTIPLES */}
            {(activeTab === 'users' && selectedUsers.size > 0) && (
              <button
                onClick={() => handleBulkAction('delete', 'users')}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm"
              >
                <Trash2 className="w-4 h-4" />
                {t('admin.actions.delete')} ({selectedUsers.size})
              </button>
            )}

            <button
              onClick={refreshData}
              disabled={refreshing}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg ${buttonSecondaryClass} text-white`}
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              {t('admin.actions.refresh')}
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 p-1 bg-gray-100 dark:bg-gray-800 rounded-lg">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as any)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  activeTab === tab.key
                    ? 'bg-blue-600 text-white'
                    : `${mutedTextClass} hover:${textClass} hover:bg-gray-200 dark:hover:bg-gray-700`
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="space-y-6">
          {/* Overview Tab - Mantener igual que antes */}
          {activeTab === 'overview' && (
            <>
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className={`p-6 rounded-lg shadow border ${cardClass}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-sm ${mutedTextClass}`}>
                        {t('admin.stats.uptime')}
                      </p>
                      <p className={`text-2xl font-bold ${textClass}`}>
                        {systemStats?.server?.uptime 
                          ? formatUptime(systemStats.server.uptime)
                          : '3m'
                        }
                      </p>
                    </div>
                    <Server className="w-8 h-8 text-blue-500" />
                  </div>
                </div>

                <div className={`p-6 rounded-lg shadow border ${cardClass}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-sm ${mutedTextClass}`}>
                        {t('admin.stats.equipmentCount')}
                      </p>
                      <p className={`text-2xl font-bold ${textClass}`}>
                        {equipment.length || 2}
                      </p>
                    </div>
                    <Database className="w-8 h-8 text-green-500" />
                  </div>
                </div>

                <div className={`p-6 rounded-lg shadow border ${cardClass}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-sm ${mutedTextClass}`}>
                        {t('admin.stats.searchesToday')}
                      </p>
                      <p className={`text-2xl font-bold ${textClass}`}>
                        {searchStats.length || 'n'}
                      </p>
                    </div>
                    <Search className="w-8 h-8 text-purple-500" />
                  </div>
                </div>

                <div className={`p-6 rounded-lg shadow border ${cardClass}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-sm ${mutedTextClass}`}>
                        {t('admin.stats.totalUsers')}
                      </p>
                      <p className={`text-2xl font-bold ${textClass}`}>{users.length}</p>
                    </div>
                    <Users className="w-8 h-8 text-orange-500" />
                  </div>
                </div>
              </div>

              {/* Status Cards */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className={`p-6 rounded-lg shadow border ${cardClass}`}>
                  <h3 className={`text-lg font-semibold ${textClass} mb-4`}>
                    {t('admin.server.info')}
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className={mutedTextClass}>
                        {t('admin.server.memoryUsed')}:
                      </span>
                      <span className={textClass}>
                        {systemStats?.server?.memory
                          ? formatMemory(systemStats.server.memory.used || 430 * 1024 * 1024)
                          : '430 MB'
                        }
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className={mutedTextClass}>
                        {t('admin.server.memoryTotal')}:
                      </span>
                      <span className={textClass}>
                        {systemStats?.server?.memory
                          ? formatMemory(systemStats.server.memory.total || 64.5 * 1024 * 1024 * 1024)
                          : '64.5 GB'
                        }
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className={mutedTextClass}>Node.js {t('admin.server.version')}:</span>
                      <span className={textClass}>
                        {systemStats?.server?.version || 'v22.17.0'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className={`p-6 rounded-lg shadow border ${cardClass}`}>
                  <h3 className={`text-lg font-semibold ${textClass} mb-4`}>
                    {t('admin.services.status')}
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className={mutedTextClass}>
                        {t('admin.services.database')}:
                      </span>
                      <span className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span className="text-green-600 font-medium">
                          {t('admin.services.healthy')}
                        </span>
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className={mutedTextClass}>
                        {t('admin.services.ai')}:
                      </span>
                      <span className="flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-red-500" />
                        <span className="text-red-600 font-medium">
                          {t('admin.services.error')}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ✅ USERS TAB OPTIMIZADO */}
          {activeTab === 'users' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className={`text-lg font-semibold ${textClass}`}>
                  {t('admin.users.title')} ({usersPagination.totalItems})
                </h3>
                <button
                  onClick={() => {
                    setEditingUser(null);
                    setFormErrors({});
                    setShowUserModal(true);
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg ${buttonPrimaryClass} text-white`}
                >
                  <UserPlus className="w-4 h-4" />
                  {t('admin.users.add')}
                </button>
              </div>

              {/* ✅ BÚSQUEDA Y FILTROS */}
              <SearchComponent
                placeholder={t('admin.actions.search') + " usuarios..."}
                onSearch={(query) => loadUsers(1, userFilters, usersSort)}
                filters={
                  <div className="flex gap-3">
                    <select
                      value={userFilters.status}
                      onChange={(e) => {
                        const newFilters = { ...userFilters, status: e.target.value };
                        setUserFilters(newFilters);
                        loadUsers(1, newFilters, usersSort);
                      }}
                      className={`px-3 py-2 rounded-lg border ${inputClass}`}
                    >
                      <option value="all">{t('admin.users.filters.all')}</option>
                      <option value="active">{t('admin.users.filters.active')}</option>
                      <option value="inactive">{t('admin.users.filters.inactive')}</option>
                      <option value="premium">{t('admin.users.filters.premium')}</option>
                      <option value="trial">{t('admin.users.filters.trial')}</option>
                    </select>
                    
                    <select
                      value={userFilters.role}
                      onChange={(e) => {
                        const newFilters = { ...userFilters, role: e.target.value };
                        setUserFilters(newFilters);
                        loadUsers(1, newFilters, usersSort);
                      }}
                      className={`px-3 py-2 rounded-lg border ${inputClass}`}
                    >
                      <option value="all">{t('admin.users.filters.all')}</option>
                      <option value="admin">{t('admin.users.roles.admin')}</option>
                      <option value="user">{t('admin.users.roles.user')}</option>
                      <option value="premium">{t('admin.users.roles.premium')}</option>
                    </select>
                  </div>
                }
              />

              {/* ✅ TABLA OPTIMIZADA */}
              <div className={`rounded-lg shadow border ${cardClass} overflow-hidden`}>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <tr>
                        <th className="px-4 py-3 text-left">
                          <input
                            type="checkbox"
                            checked={selectedUsers.size === users.length && users.length > 0}
                            onChange={() => handleSelectAll('users', users)}
                            className="rounded border-gray-300"
                          />
                        </th>
                        <th 
                          className={`text-left py-3 px-4 font-medium ${textClass} cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600`}
                          onClick={() => {
                            const newSort = usersSort.column === 'name' 
                              ? { column: 'name', direction: usersSort.direction === 'asc' ? 'desc' : 'asc' as 'asc' | 'desc' }
                              : { column: 'name', direction: 'asc' as 'asc' | 'desc' };
                            setUsersSort(newSort);
                            loadUsers(1, userFilters, newSort);
                          }}
                        >
                          <div className="flex items-center gap-2">
                            {t('admin.users.table.user')}
                            {usersSort.column === 'name' && (
                              usersSort.direction === 'asc' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />
                            )}
                          </div>
                        </th>
                        <th className={`text-left py-3 px-4 font-medium ${textClass}`}>
                          {t('admin.users.table.subscription')}
                        </th>
                        <th className={`text-left py-3 px-4 font-medium ${textClass}`}>
                          {t('admin.users.table.queries')}
                        </th>
                        <th className={`text-left py-3 px-4 font-medium ${textClass}`}>
                          {t('admin.users.table.lastLogin')}
                        </th>
                        <th className={`text-left py-3 px-4 font-medium ${textClass}`}>
                          {t('admin.users.table.actions')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => (
                        <tr 
                          key={user.id} 
                          className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'} hover:${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}
                        >
                          <td className="px-4 py-4">
                            <input
                              type="checkbox"
                              checked={selectedUsers.has(user.id)}
                              onChange={() => {
                                const newSelected = new Set(selectedUsers);
                                if (newSelected.has(user.id)) {
                                  newSelected.delete(user.id);
                                } else {
                                  newSelected.add(user.id);
                                }
                                setSelectedUsers(newSelected);
                              }}
                              className="rounded border-gray-300"
                            />
                          </td>
                          <td className="py-4 px-4">
                            <div>
                              <div className={`font-medium ${textClass}`}>{user.name}</div>
                              <div className={`text-sm ${mutedTextClass}`}>{user.email}</div>
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-2">
                              {user.is_premium && <Crown className="w-4 h-4 text-yellow-500" />}
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                user.subscription_status === 'active' 
                                  ? 'bg-green-100 text-green-800'
                                  : user.subscription_status === 'trial'
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {user.subscription_type}
                              </span>
                            </div>
                          </td>
                          <td className={`py-4 px-4 ${textClass}`}>
                            <div className="flex flex-col">
                              <span>{user.queries_used}/{user.queries_limit || '∞'}</span>
                              <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                                <div 
                                  className="bg-blue-600 h-2 rounded-full" 
                                  style={{ 
                                    width: `${user.queries_limit ? (user.queries_used / user.queries_limit) * 100 : 0}%` 
                                  }}
                                />
                              </div>
                            </div>
                          </td>
                          <td className={`py-4 px-4 text-sm ${mutedTextClass}`}>
                            {user.last_login ? new Date(user.last_login).toLocaleDateString() : '-'}
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex gap-2">
                              <button
                                onClick={() => {
                                  setEditingUser(user);
                                  setFormErrors({});
                                  setShowUserModal(true);
                                }}
                                className="p-1 text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900 rounded transition-colors"
                                title={t('admin.users.actions.editUser')}
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => {
                                  if (window.confirm(t('admin.users.confirmations.delete'))) {
                                    handleUserAction('delete', user.id);
                                  }
                                }}
                                className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900 rounded transition-colors"
                                title={t('admin.users.actions.deleteUser')}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* ✅ PAGINACIÓN */}
                <PaginationComponent
                  pagination={usersPagination}
                  onPageChange={(page) => {
                    loadUsers(page, userFilters, usersSort);
                  }}
                  onItemsPerPageChange={(items) => {
                    setUsersPagination(prev => ({ ...prev, itemsPerPage: items }));
                    loadUsers(1, userFilters, usersSort);
                  }}
                />
              </div>
            </div>
          )}
{activeTab === 'plans' && (
  <div className={`p-6 rounded-lg shadow border ${cardClass}`}>
    <div className="flex justify-between items-center mb-4">
      <h3 className={`text-lg font-semibold ${textClass}`}>Planes de Suscripción</h3>
      <button 
        onClick={() => {
          setEditingPlan(null);
          setShowPlanModal(true);
        }}
        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
      >
        <Plus className="w-4 h-4" />
        Nuevo Plan
      </button>
    </div>
    
    {plans.length > 0 ? (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {plans.map((plan) => (
          <div key={plan.id} className={`p-4 rounded-lg border ${darkMode ? 'border-gray-700' : 'border-gray-200'} relative group`}>
            {/* Botones de acción */}
            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="flex space-x-1">
                <button
                  onClick={() => {
                    setEditingPlan(plan);
                    setShowPlanModal(true);
                  }}
                  className="p-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                  title="Editar"
                >
                  <Edit className="w-3 h-3" />
                </button>
                <button
                  onClick={() => {
                    if (confirm(`¿Eliminar plan "${plan.name}"?`)) {
                      handlePlanAction('delete', plan.id);
                    }
                  }}
                  className="p-1 bg-red-600 text-white rounded hover:bg-red-700"
                  title="Eliminar"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>

            <div className="flex justify-between items-start mb-2 pr-16">
              <h4 className={`font-semibold ${textClass}`}>{plan.name}</h4>
              {plan.is_active && (
                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                  Activo
                </span>
              )}
            </div>
            <p className={`text-sm ${mutedTextClass} mb-3`}>{plan.description}</p>
            <div className="space-y-2">
              <p className={`text-lg font-bold ${textClass}`}>
                ${plan.price} {plan.currency}/{plan.interval}
              </p>
              {plan.price_annual && (
                <p className={`text-sm ${mutedTextClass}`}>
                  Anual: ${plan.price_annual} {plan.currency}
                </p>
              )}
              {plan.features && Array.isArray(plan.features) && (
                <ul className={`text-sm ${mutedTextClass} space-y-1`}>
                  {plan.features.slice(0, 3).map((feature, idx) => (
                    <li key={idx}>• {feature}</li>
                  ))}
                  {plan.features.length > 3 && (
                    <li className={`text-xs ${mutedTextClass}`}>
                      +{plan.features.length - 3} más...
                    </li>
                  )}
                </ul>
              )}
              <div className="text-xs space-y-1">
                <p className={mutedTextClass}>
                  Límite consultas: {plan.queries_limit === -1 ? 'Ilimitadas' : plan.queries_limit}
                </p>
                {plan.priority_support && (
                  <p className="text-purple-600">• Soporte prioritario</p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    ) : (
      <p className={`text-center py-8 ${mutedTextClass}`}>No hay planes configurados</p>
    )}
  </div>
)}

{activeTab === 'payments' && (
  <div className={`p-6 rounded-lg shadow border ${cardClass}`}>
    <h3 className={`text-lg font-semibold ${textClass} mb-4`}>Pagos y Transacciones</h3>
    {payments.length > 0 ? (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <th className={`text-left py-3 px-4 ${textClass}`}>ID</th>
              <th className={`text-left py-3 px-4 ${textClass}`}>Usuario</th>
              <th className={`text-left py-3 px-4 ${textClass}`}>Monto</th>
              <th className={`text-left py-3 px-4 ${textClass}`}>Estado</th>
              <th className={`text-left py-3 px-4 ${textClass}`}>Fecha</th>
            </tr>
          </thead>
          <tbody>
            {payments.map((payment) => (
              <tr key={payment.id} className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <td className={`py-3 px-4 ${textClass}`}>#{payment.id}</td>
                <td className={`py-3 px-4 ${mutedTextClass}`}>{payment.user_id}</td>
                <td className={`py-3 px-4 ${textClass}`}>
                  ${payment.amount} {payment.currency}
                </td>
                <td className={`py-3 px-4`}>
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    payment.status === 'completed' ? 'bg-green-100 text-green-800' :
                    payment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {payment.status}
                  </span>
                </td>
                <td className={`py-3 px-4 ${mutedTextClass}`}>
                  {new Date(payment.created_at).toLocaleDateString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : (
      <p className={`text-center py-8 ${mutedTextClass}`}>No hay pagos registrados</p>
    )}
  </div>
)}

{activeTab === 'documents' && (
  <div className={`p-6 rounded-lg shadow border ${cardClass}`}>
    <div className="flex justify-between items-center mb-4">
      <h3 className={`text-lg font-semibold ${textClass}`}>Documentos Técnicos</h3>
      <button 
        onClick={() => setShowDocumentModal(true)}
        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
        disabled={uploadingDoc}
      >
        <Upload className="w-4 h-4" />
        {uploadingDoc ? 'Subiendo...' : 'Subir Documento'}
      </button>
    </div>

    {/* Filtros */}
    <div className="flex gap-4 mb-4">
      <select 
        value={docFilters?.category || 'all'}
        onChange={(e) => setDocFilters(prev => ({...prev, category: e.target.value}))}
        className={`px-3 py-2 rounded-lg border ${inputClass || 'border-gray-300'}`}
      >
        <option value="all">Todas las categorías</option>
        <option value="manual">Manuales</option>
        <option value="specification">Especificaciones</option>
        <option value="guide">Guías</option>
        <option value="procedure">Procedimientos</option>
      </select>
      <select 
        value={docFilters?.visibility || 'all'}
        onChange={(e) => setDocFilters(prev => ({...prev, visibility: e.target.value}))}
        className={`px-3 py-2 rounded-lg border ${inputClass || 'border-gray-300'}`}
      >
        <option value="all">Todas las visibilidades</option>
        <option value="public">Públicos</option>
        <option value="private">Privados</option>
      </select>
    </div>
     {documents.length > 0 ? (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {documents.map((doc) => (
          <div key={doc.id} className={`p-4 rounded-lg border ${darkMode ? 'border-gray-700' : 'border-gray-200'} relative group`}>
            {/* Botones de acción */}
            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="flex space-x-1">
                <button
                  onClick={() => window.open(`/api/documents/${doc.id}/download`, '_blank')}
                  className="p-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                  title="Descargar"
                >
                  <Download className="w-3 h-3" />
                </button>
                <button
                  onClick={() => {
                    if (confirm(`¿Eliminar documento "${doc.title}"?`)) {
                      handleDocumentDelete(doc.id);
                    }
                  }}
                  className="p-1 bg-red-600 text-white rounded hover:bg-red-700"
                  title="Eliminar"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>

            <h4 className={`font-semibold ${textClass} mb-2 pr-16`}>{doc.title}</h4>
            <p className={`text-sm ${mutedTextClass} mb-2`}>{doc.description || 'Sin descripción'}</p>
            <div className="space-y-1 text-xs">
              <p className={mutedTextClass}>
                <span className={`px-2 py-1 rounded text-xs ${
                  doc.category === 'manual' ? 'bg-blue-100 text-blue-800' :
                  doc.category === 'specification' ? 'bg-green-100 text-green-800' :
                  doc.category === 'guide' ? 'bg-purple-100 text-purple-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {doc.category || 'general'}
                </span>
              </p>
              <p className={mutedTextClass}>Tipo: {doc.file_type || 'N/A'}</p>
              <p className={mutedTextClass}>
                Tamaño: {doc.file_size ? (doc.file_size / 1024 / 1024).toFixed(2) + ' MB' : 'N/A'}
              </p>
              <p className={mutedTextClass}>Descargas: {doc.downloads || 0}</p>
              <p className={mutedTextClass}>
                {doc.is_public ? (
                  <span className="text-green-600">🌐 Público</span>
                ) : (
                  <span className="text-orange-600">🔒 Privado</span>
                )}
              </p>
              <p className={mutedTextClass}>
                Subido: {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : 'N/A'}
              </p>
              <p className={mutedTextClass}>Por: {doc.uploaded_by || 'Sistema'}</p>
            </div>
          </div>
        ))}
      </div>
    ) : (
      <p className={`text-center py-8 ${mutedTextClass}`}>No hay documentos subidos</p>
    )}

    {/* Paginación */}
    {documentsPagination?.totalPages > 1 && (
      <div className="flex justify-center items-center space-x-2 mt-6">
        <button
          onClick={() => loadDocuments(documentsPagination.currentPage - 1, docFilters)}
          disabled={documentsPagination.currentPage === 1}
          className={`p-2 rounded ${documentsPagination.currentPage === 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-200'}`}
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className={`px-3 py-1 ${textClass}`}>
          {documentsPagination.currentPage} de {documentsPagination.totalPages}
        </span>
        <button
          onClick={() => loadDocuments(documentsPagination.currentPage + 1, docFilters)}
          disabled={documentsPagination.currentPage === documentsPagination.totalPages}
          className={`p-2 rounded ${documentsPagination.currentPage === documentsPagination.totalPages ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-200'}`}
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    )}
  </div>
)}

{activeTab === 'faq' && (
  <div className={`p-6 rounded-lg shadow border ${cardClass}`}>
    <div className="flex justify-between items-center mb-4">
      <h3 className={`text-lg font-semibold ${textClass}`}>Preguntas Frecuentes</h3>
      <button 
        onClick={() => {
          setEditingFaq(null);
          setShowFaqModal(true);
        }}
        className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2"
      >
        <Plus className="w-4 h-4" />
        Nueva FAQ
      </button>
    </div>
    
    {faqs.length > 0 ? (
      <div className="space-y-4">
        {faqs.map((faq) => (
          <div key={faq.id} className={`p-4 rounded-lg border ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'} relative group`}>
            {/* Botones de acción */}
            <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="flex space-x-1">
                <button
                  onClick={() => {
                    setEditingFaq(faq);
                    setShowFaqModal(true);
                  }}
                  className="p-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                  title="Editar"
                >
                  <Edit className="w-3 h-3" />
                </button>
                <button
                  onClick={() => {
                    if (confirm(`¿Eliminar FAQ "${faq.question}"?`)) {
                      handleFaqAction('delete', faq.id);
                    }
                  }}
                  className="p-1 bg-red-600 text-white rounded hover:bg-red-700"
                  title="Eliminar"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>

            <div className="flex justify-between items-start mb-3 pr-16">
              <h4 className={`font-semibold ${textClass} text-lg`}>{faq.question}</h4>
              <div className="flex items-center space-x-2">
                <span className={`text-xs px-2 py-1 rounded ${
                  faq.category === 'general' ? 'bg-blue-100 text-blue-800' :
                  faq.category === 'motores' ? 'bg-green-100 text-green-800' :
                  faq.category === 'sistema' ? 'bg-purple-100 text-purple-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {faq.category}
                </span>
                {faq.is_active ? (
                  <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded">
                    Activo
                  </span>
                ) : (
                  <span className="text-xs px-2 py-1 bg-gray-100 text-gray-800 rounded">
                    Inactivo
                  </span>
                )}
              </div>
            </div>
            
            <div className={`p-3 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} mb-3`}>
              <p className={`text-sm ${textClass}`}>{faq.answer}</p>
            </div>

            {/* Tags */}
            {faq.tags && Array.isArray(faq.tags) && faq.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {faq.tags.map((tag, idx) => (
                  <span key={idx} className={`text-xs px-2 py-1 rounded-full ${
                    darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-700'
                  }`}>
                    #{tag}
                  </span>
                ))}
              </div>
            )}

            <div className="flex justify-between items-center text-xs">
              <div className="space-x-4">
                <span className={mutedTextClass}>
                  Creado: {new Date(faq.created_at).toLocaleDateString()}
                </span>
                {faq.order && (
                  <span className={mutedTextClass}>Orden: {faq.order}</span>
                )}
              </div>
              {faq.helpfulness > 0 && (
                <span className="flex items-center space-x-1">
                  <span>👍</span>
                  <span className={mutedTextClass}>{faq.helpfulness}</span>
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    ) : (
      <p className={`text-center py-8 ${mutedTextClass}`}>No hay FAQs configuradas</p>
    )}
  </div>
)}
          {/* Equipment Tab - mantener igual que antes */}
          {activeTab === 'equipment' && (
            <div className={`p-6 rounded-lg shadow border ${cardClass}`}>
              <div className="flex justify-between items-center mb-4">
                <h3 className={`text-lg font-semibold ${textClass}`}>
                  {t('admin.equipment.title')}
                </h3>
                <span className={`text-sm ${mutedTextClass}`}>
                  {t('admin.equipment.count')}: {equipment.length}
                </span>
              </div>
              
              {equipment.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                        <th className={`text-left py-3 px-4 font-medium ${textClass}`}>
                          {t('admin.equipment.serial')}
                        </th>
                        <th className={`text-left py-3 px-4 font-medium ${textClass}`}>
                          {t('admin.equipment.model')}
                        </th>
                        <th className={`text-left py-3 px-4 font-medium ${textClass}`}>
                          {t('admin.equipment.type')}
                        </th>
                        <th className={`text-left py-3 px-4 font-medium ${textClass}`}>
                          {t('admin.equipment.status')}
                        </th>
                        <th className={`text-left py-3 px-4 font-medium ${textClass}`}>
                          {t('admin.equipment.lastScraped')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {equipment.map((item) => (
                        <tr 
                          key={item.id} 
                          className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'} hover:${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}
                        >
                          <td className={`py-4 px-4 ${textClass}`}>
                            {item.serial_number}
                          </td>
                          <td className={`py-4 px-4 ${textClass}`}>
                            {item.model}
                          </td>
                          <td className={`py-4 px-4 ${mutedTextClass}`}>
                            {item.equipment_type}
                          </td>
                          <td className="py-4 px-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              item.status === 'active' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {item.status}
                            </span>
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${mutedTextClass}`}>
                            {new Date(item.last_scraped).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className={`text-center py-8 ${mutedTextClass}`}>
                  {t('admin.equipment.noData')}
                </p>
              )}
            </div>
          )}

          {/* ✅ SEARCHES TAB MEJORADO */}
          {activeTab === 'searches' && (
            <div className="space-y-6">
              <div className={`p-6 rounded-lg shadow border ${cardClass}`}>
                <h3 className={`text-lg font-semibold ${textClass} mb-4`}>
                  {t('admin.searches.stats')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className={`text-3xl font-bold ${textClass}`}>{searchStats.length}</div>
                    <div className={`text-sm ${mutedTextClass}`}>{t('admin.searches.registered')}</div>
                  </div>
                  <div className="text-center">
                    <div className={`text-3xl font-bold ${textClass}`}>
                      {popularQueries.reduce((acc, q) => acc + q.search_count, 0)}
                    </div>
                    <div className={`text-sm ${mutedTextClass}`}>{t('admin.searches.searchVolume')}</div>
                  </div>
                  <div className="text-center">
                    <div className={`text-3xl font-bold text-green-600`}>98%</div>
                    <div className={`text-sm ${mutedTextClass}`}>{t('admin.searches.successRate')}</div>
                  </div>
                </div>
              </div>

              <div className={`p-6 rounded-lg shadow border ${cardClass}`}>
                <h3 className={`text-lg font-semibold ${textClass} mb-4`}>
                  {t('admin.searches.popular')}
                </h3>
                {popularQueries.length > 0 ? (
                  <div className="space-y-3">
                    {popularQueries.slice(0, 10).map((query, index) => (
                      <div key={index} className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-gray-700 last:border-b-0">
                        <div className="flex items-center gap-3">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                            index < 3 ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {index + 1}
                          </div>
                          <span className={textClass}>{query.query_text}</span>
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <span className={`px-2 py-1 rounded-full bg-blue-100 text-blue-800`}>
                            {query.search_count} {t('admin.searches.times')}
                          </span>
                          <span className={mutedTextClass}>
                            {query.avg_results} {t('admin.searches.avgResults')}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className={`${mutedTextClass} text-center py-4`}>
                    {t('admin.searches.noQueries')}
                  </p>
                )}
              </div>
            </div> 
          )}
             {activeTab === 'system' && (
              <SystemHealth />
              )}
          {/* Continuar con el resto de tabs... */}
          {/* Config Tab (mantener original) */}
          {renderConfigTab()}
        </div>

        {/* =============================================== */}
{/* 💳 MODAL DE PLANES */}
{/* =============================================== */}
{showPlanModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className={`${cardClass} p-6 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto`}>
      <div className="flex justify-between items-center mb-4">
        <h3 className={`text-lg font-semibold ${textClass}`}>
          {editingPlan ? 'Editar Plan' : 'Nuevo Plan'}
        </h3>
        <button
          onClick={() => {
            setShowPlanModal(false);
            setEditingPlan(null);
            setFormErrors({});
          }}
          className="text-gray-400 hover:text-gray-600"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          const formData = new FormData(e.target);
          const features = formData.get('features').split('\n').filter(f => f.trim());
          
          const planData = {
            plan_code: formData.get('plan_code'),
            name: formData.get('name'),
            description: formData.get('description'),
            price_monthly: parseFloat(formData.get('price_monthly')),
            price_annual: parseFloat(formData.get('price_annual')) || null,
            currency: formData.get('currency') || 'USD',
            features: features,
            is_popular: formData.get('is_popular') === 'on',
            is_active: formData.get('is_active') === 'on',
            trial_days: parseInt(formData.get('trial_days')) || 0
          };

          handlePlanAction(editingPlan ? 'update' : 'create', editingPlan?.id, planData);
        }}
        className="space-y-4"
      >
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={`block text-sm font-medium ${textClass} mb-1`}>
              Código del Plan *
            </label>
            <input
              type="text"
              name="plan_code"
              defaultValue={editingPlan?.plan_code || ''}
              required
              className={`w-full px-3 py-2 border rounded-lg ${inputClass}`}
              placeholder="ej: profesional"
            />
          </div>
          <div>
            <label className={`block text-sm font-medium ${textClass} mb-1`}>
              Nombre del Plan *
            </label>
            <input
              type="text"
              name="name"
              defaultValue={editingPlan?.name || ''}
              required
              className={`w-full px-3 py-2 border rounded-lg ${inputClass}`}
              placeholder="ej: Plan Profesional"
            />
          </div>
        </div>

        <div>
          <label className={`block text-sm font-medium ${textClass} mb-1`}>
            Descripción
          </label>
          <textarea
            name="description"
            defaultValue={editingPlan?.description || ''}
            rows="2"
            className={`w-full px-3 py-2 border rounded-lg ${inputClass}`}
            placeholder="Descripción del plan"
          />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className={`block text-sm font-medium ${textClass} mb-1`}>
              Precio Mensual *
            </label>
            <input
              type="number"
              name="price_monthly"
              defaultValue={editingPlan?.price_monthly || ''}
              step="0.01"
              required
              className={`w-full px-3 py-2 border rounded-lg ${inputClass}`}
              placeholder="29.99"
            />
          </div>
          <div>
            <label className={`block text-sm font-medium ${textClass} mb-1`}>
              Precio Anual
            </label>
            <input
              type="number"
              name="price_annual"
              defaultValue={editingPlan?.price_annual || ''}
              step="0.01"
              className={`w-full px-3 py-2 border rounded-lg ${inputClass}`}
              placeholder="299.99"
            />
          </div>
          <div>
            <label className={`block text-sm font-medium ${textClass} mb-1`}>
              Días de Prueba
            </label>
            <input
              type="number"
              name="trial_days"
              defaultValue={editingPlan?.trial_days || 0}
              className={`w-full px-3 py-2 border rounded-lg ${inputClass}`}
              placeholder="7"
            />
          </div>
        </div>

        <div>
          <label className={`block text-sm font-medium ${textClass} mb-1`}>
            Características (una por línea)
          </label>
          <textarea
            name="features"
            defaultValue={editingPlan?.features?.join('\n') || ''}
            rows="4"
            className={`w-full px-3 py-2 border rounded-lg ${inputClass}`}
            placeholder="Consultas ilimitadas&#10;Soporte prioritario&#10;Documentación especializada"
          />
        </div>

        <div className="flex space-x-6">
          <label className={`flex items-center space-x-2 ${textClass}`}>
            <input
              type="checkbox"
              name="is_popular"
              defaultChecked={editingPlan?.is_popular}
              className="rounded"
            />
            <span>Plan Popular</span>
          </label>
          <label className={`flex items-center space-x-2 ${textClass}`}>
            <input
              type="checkbox"
              name="is_active"
              defaultChecked={editingPlan?.is_active !== false}
              className="rounded"
            />
            <span>Activo</span>
          </label>
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={() => {
              setShowPlanModal(false);
              setEditingPlan(null);
              setFormErrors({});
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {isSubmitting ? 'Guardando...' : (editingPlan ? 'Actualizar' : 'Crear')}
          </button>
        </div>
      </form>
    </div>
  </div>
)}

{/* =============================================== */}
{/* 📄 MODAL DE DOCUMENTOS */}
{/* =============================================== */}
{showDocumentModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className={`${cardClass} p-6 rounded-lg w-full max-w-lg`}>
      <div className="flex justify-between items-center mb-4">
        <h3 className={`text-lg font-semibold ${textClass}`}>Subir Documento</h3>
        <button
          onClick={() => setShowDocumentModal(false)}
          className="text-gray-400 hover:text-gray-600"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      <form
        onSubmit={async (e) => {
          e.preventDefault();
          const formData = new FormData(e.target);
          const file = formData.get('file');
          
          if (!file || file.size === 0) {
            addNotification('error', 'Por favor selecciona un archivo');
            return;
          }

          const metadata = {
            title: formData.get('title') || file.name,
            description: formData.get('description') || '',
            category: formData.get('category') || 'manual',
            is_public: formData.get('is_public') === 'on',
            version: formData.get('version') || '1.0'
          };

          setUploadingDoc(true);
          try {
            await handleDocumentUpload(file, metadata);
            setShowDocumentModal(false);
          } catch (error) {
            console.error('Error uploading:', error);
          } finally {
            setUploadingDoc(false);
          }
        }}
        className="space-y-4"
      >
        <div>
          <label className={`block text-sm font-medium ${textClass} mb-1`}>
            Archivo *
          </label>
          <input
            type="file"
            name="file"
            required
            accept=".pdf,.doc,.docx,.txt,.md"
            className={`w-full px-3 py-2 border rounded-lg ${inputClass}`}
          />
          <p className={`text-xs ${mutedTextClass} mt-1`}>
            Formatos: PDF, DOC, DOCX, TXT, MD (máx. 10MB)
          </p>
        </div>

        <div>
          <label className={`block text-sm font-medium ${textClass} mb-1`}>
            Título
          </label>
          <input
            type="text"
            name="title"
            className={`w-full px-3 py-2 border rounded-lg ${inputClass}`}
            placeholder="Se usará el nombre del archivo si se deja vacío"
          />
        </div>

        <div>
          <label className={`block text-sm font-medium ${textClass} mb-1`}>
            Descripción
          </label>
          <textarea
            name="description"
            rows="2"
            className={`w-full px-3 py-2 border rounded-lg ${inputClass}`}
            placeholder="Descripción del documento"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={`block text-sm font-medium ${textClass} mb-1`}>
              Categoría
            </label>
            <select
              name="category"
              className={`w-full px-3 py-2 border rounded-lg ${inputClass}`}
            >
              <option value="manual">Manual</option>
              <option value="specification">Especificación</option>
              <option value="guide">Guía</option>
              <option value="procedure">Procedimiento</option>
            </select>
          </div>
          <div>
            <label className={`block text-sm font-medium ${textClass} mb-1`}>
              Versión
            </label>
            <input
              type="text"
              name="version"
              defaultValue="1.0"
              className={`w-full px-3 py-2 border rounded-lg ${inputClass}`}
              placeholder="1.0"
            />
          </div>
        </div>

        <div>
          <label className={`flex items-center space-x-2 ${textClass}`}>
            <input
              type="checkbox"
              name="is_public"
              defaultChecked
              className="rounded"
            />
            <span>Documento público</span>
          </label>
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={() => setShowDocumentModal(false)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={uploadingDoc}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
          >
            {uploadingDoc ? 'Subiendo...' : 'Subir Documento'}
          </button>
        </div>
      </form>
    </div>
  </div>
)}

{/* =============================================== */}
{/* ❓ MODAL DE FAQ */}
{/* =============================================== */}
{showFaqModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className={`${cardClass} p-6 rounded-lg w-full max-w-2xl`}>
      <div className="flex justify-between items-center mb-4">
        <h3 className={`text-lg font-semibold ${textClass}`}>
          {editingFaq ? 'Editar FAQ' : 'Nueva FAQ'}
        </h3>
        <button
          onClick={() => {
            setShowFaqModal(false);
            setEditingFaq(null);
            setFormErrors({});
          }}
          className="text-gray-400 hover:text-gray-600"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          const formData = new FormData(e.target);
          const tags = formData.get('tags').split(',').map(t => t.trim()).filter(t => t);
          
          const faqData = {
            question: formData.get('question'),
            answer: formData.get('answer'),
            category: formData.get('category'),
            tags: tags,
            order: parseInt(formData.get('order')) || 0,
            is_active: formData.get('is_active') === 'on'
          };

          handleFaqAction(editingFaq ? 'update' : 'create', editingFaq?.id, faqData);
        }}
        className="space-y-4"
      >
        <div>
          <label className={`block text-sm font-medium ${textClass} mb-1`}>
            Pregunta *
          </label>
          <input
            type="text"
            name="question"
            defaultValue={editingFaq?.question || ''}
            required
            className={`w-full px-3 py-2 border rounded-lg ${inputClass}`}
            placeholder="¿Cómo usar el sistema técnico?"
          />
        </div>

        <div>
          <label className={`block text-sm font-medium ${textClass} mb-1`}>
            Respuesta *
          </label>
          <textarea
            name="answer"
            defaultValue={editingFaq?.answer || ''}
            required
            rows="4"
            className={`w-full px-3 py-2 border rounded-lg ${inputClass}`}
            placeholder="La respuesta detallada a la pregunta..."
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={`block text-sm font-medium ${textClass} mb-1`}>
              Categoría
            </label>
            <select
              name="category"
              defaultValue={editingFaq?.category || 'general'}
              className={`w-full px-3 py-2 border rounded-lg ${inputClass}`}
            >
              <option value="general">General</option>
              <option value="motores">Motores</option>
              <option value="sistema">Sistema</option>
              <option value="tecnico">Técnico</option>
              <option value="soporte">Soporte</option>
            </select>
          </div>
          <div>
            <label className={`block text-sm font-medium ${textClass} mb-1`}>
              Orden
            </label>
            <input
              type="number"
              name="order"
              defaultValue={editingFaq?.order || 0}
              className={`w-full px-3 py-2 border rounded-lg ${inputClass}`}
              placeholder="0"
            />
          </div>
        </div>

        <div>
          <label className={`block text-sm font-medium ${textClass} mb-1`}>
            Tags (separados por coma)
          </label>
          <input
            type="text"
            name="tags"
            defaultValue={editingFaq?.tags?.join(', ') || ''}
            className={`w-full px-3 py-2 border rounded-lg ${inputClass}`}
            placeholder="caterpillar, motor, técnico"
          />
        </div>

        <div>
          <label className={`flex items-center space-x-2 ${textClass}`}>
            <input
              type="checkbox"
              name="is_active"
              defaultChecked={editingFaq?.is_active !== false}
              className="rounded"
            />
            <span>Activa</span>
          </label>
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={() => {
              setShowFaqModal(false);
              setEditingFaq(null);
              setFormErrors({});
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
          >
            {isSubmitting ? 'Guardando...' : (editingFaq ? 'Actualizar' : 'Crear')}
          </button>
        </div>
      </form>
    </div>
  </div>
)}

        {/* ✅ MODAL DE USUARIO OPTIMIZADO CON VALIDACIÓN */}
        {showUserModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className={`${cardClass} rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto`}>
              <div className="flex justify-between items-center mb-6">
                <h3 className={`text-lg font-semibold ${textClass}`}>
                  {editingUser ? t('admin.users.edit') : t('admin.users.add')}
                </h3>
                <button
                  onClick={() => {
                    setShowUserModal(false);
                    setEditingUser(null);
                    setFormErrors({});
                  }}
                  className={`text-gray-500 hover:text-gray-700`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                const userData = {
                  name: formData.get('name') as string,
                  email: formData.get('email') as string,
                  role: formData.get('role') as string,
                  subscription_status: formData.get('status') as string,
                  subscription_type: formData.get('subscription') as string,
                  queries_limit: parseInt(formData.get('queriesLimit') as string) || 0,
                  phone: formData.get('phone') as string,
                  company: formData.get('company') as string
                };
                handleUserAction(editingUser ? 'update' : 'create', editingUser?.id, userData);
              }}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className={`block text-sm font-medium ${textClass} mb-2`}>
                      {t('admin.users.form.name')}
                    </label>
                    <input
                      type="text"
                      name="name"
                      defaultValue={editingUser?.name || ''}
                      className={`w-full px-3 py-2 rounded-lg border ${inputClass} focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        formErrors.name ? 'border-red-500' : ''
                      }`}
                    />
                    {formErrors.name && (
                      <p className="text-red-500 text-sm mt-1">{formErrors.name}</p>
                    )}
                  </div>

                  <div>
                    <label className={`block text-sm font-medium ${textClass} mb-2`}>
                      {t('admin.users.form.email')}
                    </label>
                    <input
                      type="email"
                      name="email"
                      defaultValue={editingUser?.email || ''}
                      className={`w-full px-3 py-2 rounded-lg border ${inputClass} focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        formErrors.email ? 'border-red-500' : ''
                      }`}
                    />
                    {formErrors.email && (
                      <p className="text-red-500 text-sm mt-1">{formErrors.email}</p>
                    )}
                  </div>

                  <div>
                    <label className={`block text-sm font-medium ${textClass} mb-2`}>
                      {t('admin.users.form.role')}
                    </label>
                    <select
                      name="role"
                      defaultValue={editingUser?.role || 'user'}
                      className={`w-full px-3 py-2 rounded-lg border ${inputClass} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                    >
                      <option value="user">{t('admin.users.roles.user')}</option>
                      <option value="premium">{t('admin.users.roles.premium')}</option>
                      <option value="moderator">{t('admin.users.roles.moderator')}</option>
                      <option value="admin">{t('admin.users.roles.admin')}</option>
                    </select>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium ${textClass} mb-2`}>
                      {t('admin.users.form.status')}
                    </label>
                    <select
                      name="status"
                      defaultValue={editingUser?.subscription_status || 'active'}
                      className={`w-full px-3 py-2 rounded-lg border ${inputClass} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                    >
                      <option value="active">{t('admin.users.status.active')}</option>
                      <option value="inactive">{t('admin.users.status.inactive')}</option>
                      <option value="trial">{t('admin.users.filters.trial')}</option>
                      <option value="suspended">{t('admin.users.status.suspended')}</option>
                    </select>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium ${textClass} mb-2`}>
                      {t('admin.users.form.queriesLimit')}
                    </label>
                    <input
                      type="number"
                      name="queriesLimit"
                      min="0"
                      defaultValue={editingUser?.queries_limit || 100}
                      className={`w-full px-3 py-2 rounded-lg border ${inputClass} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium ${textClass} mb-2`}>
                      {t('admin.users.form.phone')}
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      defaultValue={editingUser?.phone || ''}
                      className={`w-full px-3 py-2 rounded-lg border ${inputClass} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label className={`block text-sm font-medium ${textClass} mb-2`}>
                    {t('admin.users.form.company')}
                  </label>
                  <input
                    type="text"
                    name="company"
                    defaultValue={editingUser?.company || ''}
                    className={`w-full px-3 py-2 rounded-lg border ${inputClass} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                  />
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowUserModal(false);
                      setEditingUser(null);
                      setFormErrors({});
                    }}
                    className={`px-4 py-2 rounded-lg ${buttonSecondaryClass} text-white`}
                    disabled={isSubmitting}
                  >
                    {t('admin.actions.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`px-4 py-2 rounded-lg ${buttonPrimaryClass} text-white flex items-center gap-2`}
                  >
                    {isSubmitting ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        {t('admin.notifications.loading')}
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        {t('admin.actions.save')}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* ✅ COMPONENTE DE NOTIFICACIONES */}
      <NotificationComponent />
    </div>
  );
};

export default AdminDashboard;